import { rootRouteId } from '@tanstack/react-router';

import { hardNavigate } from './browser';
import { documentKey } from './documents';
import { isProbeable, LEGACY_PARAM } from './probe';
import { hasUnsavedChanges } from './unsavedChanges';

import type { AnyRoute, AnyRouter } from '@tanstack/react-router';

import type { RouteLeavePolicy, SingleRootStaticData } from './types';

export const staticDataOf = (route: AnyRoute | undefined): Partial<SingleRootStaticData> =>
  route?.options.staticData ?? {};

const segmentsOf = (path: string): string[] => path.split('/').filter(Boolean);

// getMatchedRoutes falls back to the deepest prefix match, so `/admin/jobs/$jobId` would claim
// `/admin/jobs/5/attachments`. Only a route whose full path covers every segment owns the URL;
// a trailing splat (`/$`) covers the rest.
const coversPath = (route: AnyRoute, pathname: string): boolean => {
  const routeSegments = segmentsOf(route.fullPath);
  const pathSegments = segmentsOf(pathname);
  const splat = routeSegments.at(-1) === '$';
  const fixed = splat ? routeSegments.slice(0, -1) : routeSegments;
  if (splat ? pathSegments.length < fixed.length : pathSegments.length !== fixed.length) {
    return false;
  }
  return fixed.every(
    (segment, index) => segment.startsWith('$') || segment === pathSegments.at(index)
  );
};

// The admitted route for a pathname, or undefined when the shell has no route for it.
export const admittedRouteFor = (router: AnyRouter, pathname: string): AnyRoute | undefined => {
  const { foundRoute } = router.getMatchedRoutes(pathname);
  if (!foundRoute || foundRoute.id === rootRouteId) return undefined;
  return coversPath(foundRoute, pathname) ? foundRoute : undefined;
};

export const isAdmittedPath = (router: AnyRouter, pathname: string): boolean =>
  admittedRouteFor(router, pathname) !== undefined;

// A gap page has no leave prompts of its own, so leaving it client side is safe. An
// admitted route keeps native unload unless it declares `leave: 'client'`.
export const currentLeavePolicy = (router: AnyRouter): RouteLeavePolicy => {
  const route = admittedRouteFor(router, router.state.location.pathname);
  if (!route) return 'client';
  return staticDataOf(route).leave ?? 'document';
};

export type ShellNavigate = (destination: URL) => boolean;

// An in-page anchor on a page the shell probes: the browser scrolls, nothing is probed again.
const isAnchorOnCurrentPage = (destination: URL): boolean =>
  destination.hash !== '' && documentKey(destination.href) === documentKey(window.location.href);

/**
 * Navigation policy for the shell. Returns true (and navigates client side) for a same-origin
 * URL that matches an admitted route, or that the shell probes (`isProbeable`: the server
 * still decides between gap, redirect, permission denial and download), while the current
 * route allows client leave. Otherwise returns false so the browser navigates natively;
 * `?legacy_ui=1` always does. Once web serves a newer build (`isUpdateReady`) and no page has
 * unsaved changes, it also returns false, so the next navigation loads the new build. Matches
 * the standalone Navbar's `navigation.navigate(destination: URL)` contract.
 */
export const createShellNavigate =
  (
    router: AnyRouter,
    leavePolicies: readonly RouteLeavePolicy[] = ['client'],
    isUpdateReady: () => boolean = () => false
  ): ShellNavigate =>
  (destination) => {
    if (destination.origin !== window.location.origin) return false;
    if (destination.searchParams.get(LEGACY_PARAM) === '1') return false;
    if (!isAdmittedPath(router, destination.pathname)) {
      if (!isProbeable(destination) || isAnchorOnCurrentPage(destination)) return false;
    }
    if (!leavePolicies.includes(currentLeavePolicy(router))) return false;
    if (isUpdateReady() && !hasUnsavedChanges()) return false;

    router
      .navigate({ href: `${destination.pathname}${destination.search}${destination.hash}` })
      .catch(() => hardNavigate(destination.href));
    return true;
  };

/**
 * Window-level click handler for same-window links in page content. It runs after every
 * document listener, so a link a page or rails-ujs already handled (defaultPrevented) is left
 * alone. The chrome navigates through its own `navigate` prop.
 */
export const createLinkClickHandler =
  (router: AnyRouter, isUpdateReady?: () => boolean) =>
  (event: MouseEvent): void => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if ((anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
    if (createShellNavigate(router, ['client', 'links'], isUpdateReady)(new URL(anchor.href))) {
      event.preventDefault();
    }
  };
